import { Button, CircularProgress, Typography } from "@aph/ui-lib-humanx";
import { intervalToDuration } from "date-fns/intervalToDuration";
import { useEffect, useRef, useState } from "react";
import BackArrowIcon from "$/icons/icon_back_arrow.svg";
import EditPhoneIcon from "$/icons/icon_phone_edit.svg";
import WhiteRightArrow from "$/icons/icon_WhiteRightArrow.svg";
import { CHECK_DIGIT_REGEX, MAX_INVALID_ATTEMPTS } from "@/components/SignIn/constants";
import styles from "./OtpInput.module.scss";

interface OtpInputProps {
  invalidOtpCount?: number;
  handleOtpSubmit: (otp: string) => void;
  handleOtpValue: (otp: string[]) => void;
  isVerifyingOtp: boolean;
  errorMessage?: string;
  phoneNumber: string;
  handleResendOtp: () => void;
  handleBackToPhoneScreen: () => void;
  isSendingOtp: boolean;
  timer: number;
  isShowCountDown: boolean;
  otp: string[];
}

const OtpInput: React.FC<OtpInputProps> = ({
  invalidOtpCount = 0,
  handleOtpSubmit,
  isVerifyingOtp,
  errorMessage,
  phoneNumber,
  handleResendOtp,
  isSendingOtp,
  handleBackToPhoneScreen,
  timer,
  isShowCountDown,
  handleOtpValue,
  otp,
}) => {
  const otpInputRefs = useRef<HTMLInputElement[]>([]);
  const [isLoginLocked, setIsLoginLocked] = useState<boolean>(false);
  const [unlockTime, setUnlockTime] = useState<any>(null);
  const [countdown, setCountdown] = useState<string>("");
  const [isSixDigits, setIsSixDigits] = useState<boolean>(false);

  const handleChange = (value: string, index: number) => {
    const otpVal = CHECK_DIGIT_REGEX.test(value) ? value : "";

    const newOtp = [...otp];
    newOtp[index] = otpVal;

    if (otpVal != "" && index < 5) {
      otpInputRefs.current[index + 1]?.focus(); // Move to the next input
    }

    handleOtpValue(newOtp);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus(); // Move to the previous input
    }
  };

  const submitOtp = (e: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    handleOtpSubmit(otp.join(""));
  };

  useEffect(() => {
    otpInputRefs.current[0]?.focus();
    const timeOutPayload = JSON.parse(localStorage.getItem("otpTimeout") || "null");

    if (timeOutPayload?.phoneNumber === phoneNumber) {
      setIsLoginLocked(true);
      setUnlockTime(new Date(timeOutPayload?.timeout));

      const initTimeLeft = new Date(timeOutPayload?.timeout).getTime() - new Date().getTime();

      if (initTimeLeft > 0) {
        const duration = intervalToDuration({ start: 0, end: initTimeLeft });
        setCountdown(
          `${duration?.minutes?.toString().padStart(2, "0")}:${duration?.seconds?.toString().padStart(2, "0")}`
        );
      }
    }
  }, [invalidOtpCount]);

  useEffect(() => {
    if (isLoginLocked && unlockTime) {
      const interval = setInterval(() => {
        const currentTime = new Date();
        const timeLeft = new Date(unlockTime).getTime() - currentTime.getTime();

        if (timeLeft <= 0) {
          clearInterval(interval);
          setIsLoginLocked(false);
          localStorage.removeItem("otpTimeout"); // Clear unlock time
        } else {
          const durationtime = intervalToDuration({ start: 0, end: timeLeft });
          setCountdown(
            `${durationtime?.minutes?.toString().padStart(2, "0")}:${durationtime?.seconds?.toString().padStart(2, "0")}`
          );
        }
      }, 1000);

      return () => clearInterval(interval); // Cleanup on unmount
    }
  }, [isLoginLocked, unlockTime]);

  useEffect(() => {
    setIsSixDigits(otp.length === 6 && otp.every((digit) => digit !== ""));
  }, [otp]);

  return (
    <div className={styles.otpRoot}>
      <div className={`${styles.titleBx} ${styles.whenShowBackArrow}`}>
        <span className={styles.backArrow} onClick={handleBackToPhoneScreen}>
          <BackArrowIcon />
        </span>
        {isLoginLocked && (
          <>
            <div className={styles.oppsTitle}>{`Oops!`}</div>
            <div className={styles.oppsSubtitle}>{`You entered an incorrect OTP ${MAX_INVALID_ATTEMPTS} times. Try
again after sometime!`}</div>
          </>
        )}
        {!isLoginLocked && (
          <>
            <Typography component="h2" className={styles.title}>
              Enter the OTP sent to
            </Typography>
            <Typography component="p" className={styles.editNumber}>
              {phoneNumber}
              <span className={styles.icon} onClick={handleBackToPhoneScreen}>
                <EditPhoneIcon />
              </span>
            </Typography>
          </>
        )}
      </div>
      {!isLoginLocked && (
        <>
          <div className={styles.phoneInputBx}>
            <div className={`${styles.otpContainer} ${invalidOtpCount > 0 ? styles.inputError : ""}`}>
              {[...Array(6)].map((_, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    otpInputRefs.current[index] = el;
                  }}
                  name={`digit${index + 1}`}
                  type="tel"
                  value={otp[index]}
                  maxLength={1}
                  autoComplete="off"
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  autoFocus={index === 0}
                  className={styles.input}
                />
              ))}
            </div>

            <Button
              className={`${styles.btn} ${!isSixDigits ? styles.btnDisabled : ""}`}
              onClick={submitOtp}
              disabled={isVerifyingOtp || isSendingOtp || !isSixDigits}
            >
              {isVerifyingOtp || isSendingOtp ? (
                <CircularProgress width={30} height={30} color="secondary" />
              ) : (
                <WhiteRightArrow />
              )}
            </Button>
          </div>
          <div className={`${styles.extraInfo} ${styles.otpExtraInfo}`}>
            {isShowCountDown || errorMessage ? (
              <Typography component="p" className={`${styles.subText} ${errorMessage ? styles.errorMsg : ""} `}>
                {errorMessage || "Resend OTP"} <strong> in {timer} seconds</strong>
              </Typography>
            ) : (
              <>
                <Typography component="p" className={styles.subText}>
                  Didn’t receive the code?
                </Typography>
                <div className={styles.resendOtpText} onClick={() => handleResendOtp()}>
                  Resend OTP
                </div>
              </>
            )}
          </div>
        </>
      )}
      {isLoginLocked && (
        <div className={`${styles.tryagain}`}>
          <Typography component="p" className={styles.ootimer}>
            {`Try again after ${countdown}`}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default OtpInput;
